import { useState, useEffect, useMemo, useRef } from "react";
import { useFgp } from "@/context/FgpContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  Calculator, Percent, Clock, ArrowRightLeft, CalendarClock, Ruler, Users, Timer,
  RefreshCw, Plus, X, ArrowLeftRight,
} from "lucide-react";
import { toast } from "sonner";

const lsGet = (k, d) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* */ } };

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-slate-700 text-sm">{label}</Label>{children}</div>
);
const Result = ({ label, value, big, testid }) => (
  <div className={`rounded-lg border border-slate-200 p-3 ${big ? "bg-neutral-50" : "bg-white"}`}>
    <p className="text-xs text-slate-400">{label}</p>
    <p className={`font-heading font-semibold text-slate-900 mt-0.5 ${big ? "text-2xl" : "text-lg"}`} data-testid={testid}>{value}</p>
  </div>
);

// Live "now" ticking hook
const useNow = (ms = 1000) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), ms); return () => clearInterval(t); }, [ms]);
  return now;
};

const CITIES = [
  { city: "London", tz: "Europe/London" },
  { city: "Jakarta", tz: "Asia/Jakarta" },
  { city: "New York", tz: "America/New_York" },
  { city: "Geneva", tz: "Europe/Zurich" },
  { city: "Dubai", tz: "Asia/Dubai" },
  { city: "Singapore", tz: "Asia/Singapore" },
  { city: "Tokyo", tz: "Asia/Tokyo" },
  { city: "Los Angeles", tz: "America/Los_Angeles" },
  { city: "Sydney", tz: "Australia/Sydney" },
  { city: "Paris", tz: "Europe/Paris" },
  { city: "Hong Kong", tz: "Asia/Hong_Kong" },
  { city: "Mumbai", tz: "Asia/Kolkata" },
];
const zoneOffsetMin = (tz, date = new Date()) => {
  const dtf = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const p = dtf.formatToParts(date).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour === "24" ? 0 : p.hour, p.minute, p.second);
  return Math.round((asUTC - date.getTime()) / 60000);
};
const fmtTime = (tz, date) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(date);
const fmtDay = (tz, date) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short", day: "numeric", month: "short" }).format(date);

// ---------------- Tip ----------------
const TipTool = () => {
  const [bill, setBill] = useState("");
  const [pct, setPct] = useState(15);
  const [split, setSplit] = useState(1);
  const [cur, setCur] = useState("£");
  const b = parseFloat(bill) || 0;
  const tip = b * pct / 100, total = b + tip, per = total / Math.max(1, split);
  const m = (n) => `${cur}${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Field label="Bill amount"><Input type="number" value={bill} onChange={(e) => setBill(e.target.value)} placeholder="0.00" data-testid="tip-bill" /></Field>
        <Field label="Currency">
          <Select value={cur} onValueChange={setCur}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["£", "$", "€", "Rp", "CHF"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="Split between"><Input type="number" min="1" value={split} onChange={(e) => setSplit(Math.max(1, parseInt(e.target.value) || 1))} data-testid="tip-split" /></Field>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5"><Label className="text-slate-700 text-sm">Tip</Label><span className="text-sm font-semibold text-neutral-700">{pct}%</span></div>
        <div className="flex gap-1.5 mb-2">{[10, 12.5, 15, 18, 20].map((v) => (
          <button key={v} onClick={() => setPct(v)} data-testid={`tip-preset-${v}`} className={`px-2.5 py-1 rounded-lg text-xs font-medium ${pct === v ? "bg-neutral-600 text-white" : "bg-slate-100 text-slate-600"}`}>{v}%</button>
        ))}</div>
        <Slider value={[pct]} min={0} max={30} step={0.5} onValueChange={(v) => setPct(v[0])} data-testid="tip-percent" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Result label="Tip" value={m(tip)} testid="tip-value" />
        <Result label="Total" value={m(total)} testid="tip-total" />
        <Result label="Per person" value={m(per)} big testid="tip-per-person" />
      </div>
    </div>
  );
};

// ---------------- VAT / Tax ----------------
const TaxTool = () => {
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState(11);
  const [mode, setMode] = useState("add");
  const a = parseFloat(amount) || 0, r = parseFloat(rate) || 0;
  let net, tax, gross;
  if (mode === "add") { net = a; tax = a * r / 100; gross = a + tax; }
  else { gross = a; net = a / (1 + r / 100); tax = gross - net; }
  const m = (n) => n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Amount"><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="tax-amount" /></Field>
        <Field label="Rate %"><Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="tax-rate" /></Field>
      </div>
      <div className="flex gap-1.5">
        {[["11", "Indonesia 11%"], ["20", "UK 20%"], ["0", "Zero"]].map(([v, l]) => (
          <button key={v} onClick={() => setRate(Number(v))} className={`px-2.5 py-1 rounded-lg text-xs font-medium ${Number(rate) === Number(v) ? "bg-neutral-600 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <div className="flex gap-1.5 bg-slate-100 rounded-lg p-1 w-fit">
        {[["add", "Add tax"], ["remove", "Remove tax"]].map(([v, l]) => (
          <button key={v} onClick={() => setMode(v)} data-testid={`tax-mode-${v}`} className={`px-3 py-1 rounded-md text-sm font-medium ${mode === v ? "bg-white shadow-sm text-slate-800" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Result label="Net" value={m(net)} testid="tax-net" />
        <Result label="Tax" value={m(tax)} testid="tax-value" />
        <Result label="Gross" value={m(gross)} big testid="tax-gross" />
      </div>
    </div>
  );
};

// ---------------- Time-zone + World clock ----------------
const TimezoneTool = () => {
  const now = useNow(1000);
  const [cities, setCities] = useState(() => lsGet("fgp_tool_cities", ["London", "Jakarta", "New York", "Geneva"]));
  const [a, setA] = useState("London");
  const [b, setB] = useState("Jakarta");
  useEffect(() => { lsSet("fgp_tool_cities", cities); }, [cities]);
  const tzOf = (name) => CITIES.find((c) => c.city === name)?.tz;
  const diffMin = zoneOffsetMin(tzOf(a), now) - zoneOffsetMin(tzOf(b), now);
  const h = Math.floor(Math.abs(diffMin) / 60), min = Math.abs(diffMin) % 60;
  const dir = diffMin === 0 ? "the same time as" : diffMin > 0 ? "ahead of" : "behind";
  const addCity = (name) => { if (name && !cities.includes(name)) setCities([...cities, name]); };
  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-slate-700 text-sm">World clock</Label>
          <Select onValueChange={addCity}>
            <SelectTrigger className="h-8 w-40" data-testid="tz-add-city"><SelectValue placeholder="Add city…" /></SelectTrigger>
            <SelectContent>{CITIES.filter((c) => !cities.includes(c.city)).map((c) => <SelectItem key={c.city} value={c.city}>{c.city}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {cities.map((name) => (
            <div key={name} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between" data-testid={`worldclock-${name}`}>
              <div><p className="text-sm font-medium text-slate-800">{name}</p><p className="text-xs text-slate-400">{fmtDay(tzOf(name), now)}</p></div>
              <div className="text-right"><p className="font-heading text-lg font-semibold tabular-nums text-slate-900">{fmtTime(tzOf(name), now)}</p></div>
              <button onClick={() => setCities(cities.filter((x) => x !== name))} className="ml-2 text-slate-300 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
        <Label className="text-slate-700 text-sm">Difference between two places</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Select value={a} onValueChange={setA}><SelectTrigger data-testid="tz-city-a"><SelectValue /></SelectTrigger>
            <SelectContent>{CITIES.map((c) => <SelectItem key={c.city} value={c.city}>{c.city}</SelectItem>)}</SelectContent></Select>
          <Select value={b} onValueChange={setB}><SelectTrigger data-testid="tz-city-b"><SelectValue /></SelectTrigger>
            <SelectContent>{CITIES.map((c) => <SelectItem key={c.city} value={c.city}>{c.city}</SelectItem>)}</SelectContent></Select>
        </div>
        <p className="mt-3 text-sm text-slate-700" data-testid="tz-diff-result">
          <strong>{a}</strong> is <strong>{diffMin === 0 ? "" : `${h}h${min ? ` ${min}m` : ""} `}{dir}</strong> <strong>{b}</strong>.
          <span className="text-slate-400"> ({fmtTime(tzOf(a), now)} vs {fmtTime(tzOf(b), now)})</span>
        </p>
      </div>
    </div>
  );
};

// ---------------- Currency (live) ----------------
const CURRENCIES = ["GBP", "USD", "EUR", "IDR", "CHF", "SGD", "JPY", "AUD", "CAD", "INR", "CNY"];
async function fetchRate(from, to) {
  if (from === to) return 1;
  try {
    const r = await fetch(`https://open.er-api.com/v6/latest/${from}`);
    const d = await r.json();
    if (d?.rates?.[to]) return d.rates[to];
    throw new Error("no rate");
  } catch (e) {
    const r2 = await fetch(`https://api.frankfurter.app/latest?from=${from}&to=${to}`);
    const d2 = await r2.json();
    if (d2?.rates?.[to]) return d2.rates[to];
    throw e;
  }
}
const fmtFx = (n) => n < 1 ? n.toLocaleString("en-GB", { maximumFractionDigits: 6 }) : n.toLocaleString("en-GB", { maximumFractionDigits: 2 });
const CurrencyTool = () => {
  const [amount, setAmount] = useState("100");
  const [from, setFrom] = useState("GBP");
  const [to, setTo] = useState("IDR");
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [cached, setCached] = useState(false);
  const [updated, setUpdated] = useState(null);

  const load = async () => {
    setLoading(true); setError(false); setCached(false);
    const key = `fgp_fx_${from}_${to}`;
    try {
      const r = await fetchRate(from, to);
      setRate(r); setUpdated(new Date());
      lsSet(key, { rate: r, ts: Date.now() });
    } catch {
      const c = lsGet(key, null);
      if (c) { setRate(c.rate); setUpdated(new Date(c.ts)); setCached(true); }
      else setError(true);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);
  useEffect(() => { const t = setInterval(load, 5 * 60 * 1000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [from, to]);

  const a = parseFloat(amount) || 0;
  const converted = rate != null ? a * rate : null;
  const swap = () => { setFrom(to); setTo(from); };
  return (
    <div className="space-y-4">
      <Field label="Amount"><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="fx-amount" /></Field>
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <Field label="From">
          <Select value={from} onValueChange={setFrom}><SelectTrigger data-testid="fx-from"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </Field>
        <button onClick={swap} className="h-10 w-10 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 mb-0.5" data-testid="fx-swap"><ArrowLeftRight className="h-4 w-4" /></button>
        <Field label="To">
          <Select value={to} onValueChange={setTo}><SelectTrigger data-testid="fx-to"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </Field>
      </div>
      <div className="rounded-lg bg-neutral-50 border border-slate-200 p-4">
        {error ? <p className="text-sm text-rose-600" data-testid="fx-result">Couldn't fetch a live rate. Check your connection.</p> : (
          <>
            <p className="text-xs text-slate-400">{a.toLocaleString()} {from} =</p>
            <p className="font-heading text-2xl font-semibold text-slate-900 mt-0.5" data-testid="fx-result">
              {loading && rate == null ? "…" : `${converted != null ? fmtFx(converted) : "—"} ${to}`}
            </p>
          </>
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span data-testid="fx-rate-note">{rate != null && `1 ${from} = ${rate.toLocaleString("en-GB", { maximumFractionDigits: 4 })} ${to}`}{updated && ` · as of ${updated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`}{cached && " (cached)"}</span>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5 h-7" data-testid="fx-refresh"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
      </div>
    </div>
  );
};

// ---------------- Meeting planner ----------------
const MeetingTool = () => {
  const now = useNow(60000);
  const [sel, setSel] = useState(["London", "Jakarta", "New York"]);
  const tzOf = (name) => CITIES.find((c) => c.city === name)?.tz;
  const toggle = (name) => setSel((s) => s.includes(name) ? s.filter((x) => x !== name) : (s.length < 4 ? [...s, name] : s));
  const grid = useMemo(() => {
    const rows = sel.map((name) => {
      const off = zoneOffsetMin(tzOf(name), now) / 60;
      const cells = Array.from({ length: 24 }, (_, utc) => { const lh = ((utc + off) % 24 + 24) % 24; return { lh: Math.floor(lh), work: Math.floor(lh) >= 9 && Math.floor(lh) < 18 }; });
      return { name, cells };
    });
    const overlap = Array.from({ length: 24 }, (_, utc) => rows.every((r) => r.cells[utc].work));
    return { rows, overlap };
  }, [sel, now]);
  const windows = grid.overlap.reduce((acc, ok, utc) => { if (ok) acc.push(utc); return acc; }, []);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {CITIES.map((c) => (
          <button key={c.city} onClick={() => toggle(c.city)} data-testid={`meeting-city-${c.city}`}
            className={`px-2.5 py-1 rounded-full text-xs font-medium ${sel.includes(c.city) ? "bg-neutral-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{c.city}</button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[560px] space-y-1">
          <div className="grid grid-cols-[90px_repeat(24,1fr)] gap-0.5 text-[9px] text-slate-400">
            <span />{Array.from({ length: 24 }, (_, h) => <span key={h} className="text-center">{String(h).padStart(2, "0")}</span>)}
          </div>
          {grid.rows.map((r) => (
            <div key={r.name} className="grid grid-cols-[90px_repeat(24,1fr)] gap-0.5 items-center" data-testid={`meeting-row-${r.name}`}>
              <span className="text-xs font-medium text-slate-700 truncate">{r.name}</span>
              {r.cells.map((c, utc) => (
                <span key={utc} className={`h-5 rounded-sm ${grid.overlap[utc] ? "bg-emerald-500" : c.work ? "bg-neutral-300" : "bg-slate-100"}`} title={`${c.lh}:00 local`} />
              ))}
            </div>
          ))}
          <div className="flex gap-3 text-[10px] text-slate-400 pt-1">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> All working (best)</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-neutral-300" /> Working hrs</span>
            <span className="text-slate-400">Columns = UTC hour</span>
          </div>
        </div>
      </div>
      <p className="text-sm text-slate-700" data-testid="meeting-result">
        {windows.length ? <>Best overlap (working hours for all): <strong>{`${String(windows[0]).padStart(2, "0")}:00–${String(windows[windows.length - 1] + 1).padStart(2, "0")}:00 UTC`}</strong>.</> : "No overlapping working-hour window for the selected cities."}
      </p>
    </div>
  );
};

// ---------------- Unit converter ----------------
const UNITS = {
  Length: { m: 1, km: 1000, mi: 1609.34, ft: 0.3048, cm: 0.01 },
  Weight: { kg: 1, g: 0.001, lb: 0.453592, oz: 0.0283495 },
  Volume: { L: 1, ml: 0.001, gal: 3.78541 },
};
const UnitTool = () => {
  const [cat, setCat] = useState("Length");
  const [val, setVal] = useState("1");
  const keys = cat === "Temperature" ? ["°C", "°F"] : Object.keys(UNITS[cat]);
  const [from, setFrom] = useState(keys[0]);
  const [to, setTo] = useState(keys[1]);
  useEffect(() => { const k = cat === "Temperature" ? ["°C", "°F"] : Object.keys(UNITS[cat]); setFrom(k[0]); setTo(k[1]); }, [cat]);
  const v = parseFloat(val) || 0;
  let out;
  if (cat === "Temperature") out = from === to ? v : from === "°C" ? v * 9 / 5 + 32 : (v - 32) * 5 / 9;
  else out = v * UNITS[cat][from] / UNITS[cat][to];
  return (
    <div className="space-y-4">
      <Field label="Category">
        <Select value={cat} onValueChange={setCat}><SelectTrigger data-testid="unit-category"><SelectValue /></SelectTrigger>
          <SelectContent>{["Length", "Weight", "Volume", "Temperature"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
      </Field>
      <div className="grid grid-cols-[1fr_1fr] gap-3">
        <Field label="From"><Input type="number" value={val} onChange={(e) => setVal(e.target.value)} data-testid="unit-value" /></Field>
        <Field label="Unit">
          <Select value={from} onValueChange={setFrom}><SelectTrigger data-testid="unit-from"><SelectValue /></SelectTrigger>
            <SelectContent>{(cat === "Temperature" ? ["°C", "°F"] : Object.keys(UNITS[cat])).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
        </Field>
      </div>
      <Field label="To unit">
        <Select value={to} onValueChange={setTo}><SelectTrigger data-testid="unit-to"><SelectValue /></SelectTrigger>
          <SelectContent>{(cat === "Temperature" ? ["°C", "°F"] : Object.keys(UNITS[cat])).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
      </Field>
      <Result label={`${v} ${from} =`} value={`${out.toLocaleString("en-GB", { maximumFractionDigits: 4 })} ${to}`} big testid="unit-result" />
    </div>
  );
};

// ---------------- Expense splitter / per-diem ----------------
const SplitTool = () => {
  const [total, setTotal] = useState("");
  const [people, setPeople] = useState(2);
  const [days, setDays] = useState(1);
  const [cur, setCur] = useState("£");
  const t = parseFloat(total) || 0;
  const perPerson = t / Math.max(1, people);
  const perDay = t / Math.max(1, days);
  const m = (n) => `${cur}${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Total amount"><Input type="number" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="0.00" data-testid="split-total" /></Field>
        <Field label="Currency">
          <Select value={cur} onValueChange={setCur}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["£", "$", "€", "Rp", "CHF"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Split between (people)"><Input type="number" min="1" value={people} onChange={(e) => setPeople(Math.max(1, parseInt(e.target.value) || 1))} data-testid="split-people" /></Field>
        <Field label="Over (days)"><Input type="number" min="1" value={days} onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 1))} data-testid="split-days" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Result label="Per person" value={m(perPerson)} big testid="split-per-person" />
        <Result label="Per day (per-diem)" value={m(perDay)} big testid="split-per-day" />
      </div>
    </div>
  );
};

// ---------------- Countdown ----------------
const CountdownTool = () => {
  const now = useNow(1000);
  const { events, renewals, addTask } = useFgp();
  const [target, setTarget] = useState("");
  const [label, setLabel] = useState("Deadline");
  const picks = [
    ...events.map((e) => ({ id: e.id, label: e.title, dt: `${e.date}T${/^\d{2}:\d{2}$/.test(e.time) ? e.time : "09:00"}` })),
    ...renewals.map((r) => ({ id: r.id, label: `Renewal: ${r.title}`, dt: `${r.due}T09:00` })),
  ];
  const applyPick = (id) => { const p = picks.find((x) => x.id === id); if (p) { setTarget(p.dt); setLabel(p.label); } };
  const diff = target ? new Date(target).getTime() - now.getTime() : null;
  const passed = diff != null && diff < 0;
  const d = diff != null ? Math.abs(diff) : 0;
  const days = Math.floor(d / 86400000), hrs = Math.floor(d % 86400000 / 3600000), mins = Math.floor(d % 3600000 / 60000), secs = Math.floor(d % 60000 / 1000);
  return (
    <div className="space-y-4">
      <Field label="Quick pick from your workspace">
        <Select onValueChange={applyPick}><SelectTrigger data-testid="countdown-pick"><SelectValue placeholder="Pick an event or renewal…" /></SelectTrigger>
          <SelectContent>{picks.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent></Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Label"><Input value={label} onChange={(e) => setLabel(e.target.value)} /></Field>
        <Field label="Target date & time"><Input type="datetime-local" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="countdown-target" /></Field>
      </div>
      {target ? (
        <div className="rounded-lg bg-neutral-50 border border-slate-200 p-4 text-center">
          <p className="text-xs text-slate-400 mb-2">{label} {passed ? "was" : "in"}</p>
          <div className="flex justify-center gap-4" data-testid="countdown-result">
            {[["Days", days], ["Hrs", hrs], ["Min", mins], ["Sec", secs]].map(([l, v]) => (
              <div key={l}><p className="font-heading text-2xl font-semibold text-slate-900 tabular-nums">{v}</p><p className="text-[10px] uppercase tracking-wider text-slate-400">{l}</p></div>
            ))}
          </div>
          {passed && <p className="text-xs text-rose-600 mt-2">This date has passed.</p>}
          <Button variant="outline" size="sm" className="mt-4 gap-1.5" data-testid="countdown-remind"
            onClick={() => { addTask({ title: `Reminder: ${label}`, due: target.slice(0, 10), priority: "high" }); toast.success("Reminder task created"); }}>
            <Plus className="h-3.5 w-3.5" /> Create reminder task
          </Button>
        </div>
      ) : <p className="text-sm text-slate-400 text-center py-6">Pick a date to start the countdown.</p>}
    </div>
  );
};

export const TOOLS = [
  { id: "tip", name: "Tip Calculator", desc: "Gratuity, total & split per person", icon: Calculator, Component: TipTool },
  { id: "tax", name: "VAT / Tax Calculator", desc: "Add or extract tax at any rate", icon: Percent, Component: TaxTool },
  { id: "timezone", name: "Time Zones & World Clock", desc: "Live clocks & difference between cities", icon: Clock, Component: TimezoneTool },
  { id: "currency", name: "Currency Converter", desc: "Live exchange rates", icon: ArrowRightLeft, Component: CurrencyTool },
  { id: "meeting", name: "Meeting Planner", desc: "Find the best time across zones", icon: CalendarClock, Component: MeetingTool },
  { id: "units", name: "Unit Converter", desc: "Length, weight, temperature, volume", icon: Ruler, Component: UnitTool },
  { id: "split", name: "Expense Splitter", desc: "Split totals & per-diem", icon: Users, Component: SplitTool },
  { id: "countdown", name: "Countdown Timer", desc: "Live count to any deadline", icon: Timer, Component: CountdownTool },
];
